import type { EditorJsData } from './editorjs-types';

import { useMutation } from '@apollo/client/react';
import {
  Popover,
  Portal,
  Button,
  Spinner,
  VStack,
  Textarea,
  Text,
  HStack,
  Box,
  DialogRoot,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  DialogActionTrigger,
} from '@chakra-ui/react';
import React from 'react';
import { FiZap } from 'react-icons/fi';

import { useEditDocument } from './edit-document-context';
import { parseEditorJs } from './editorjs-types';

import { CenteredDialogContent } from '#src/components/ui/centered-dialog-content';
import { toaster } from '#src/components/ui/toaster';
import { EXECUTE_PROCESSOR } from '#src/graphql/mutations/execute-processor';
import { useAppSettings } from '#src/providers/app-settings-provider';

const DOCUMENT_AI_EDIT_PROCESSOR_ID = 'edit-document-prompt';
const ASK_DOCUMENT_PROCESSOR_ID = 'ask-document-prompt';

/** Matches server default when app settings are unavailable */
const FALLBACK_DEFAULT_EDIT_MODEL = 'gpt-4.1-nano';

/** Keep in sync with edit-document-prompt.ts DEFAULT_EDIT_SUMMARY_MESSAGE */
const FALLBACK_AI_EDIT_TOAST_DESCRIPTION =
  'AI edit applied to editor; save to persist.';

export interface PromptCommandBaseProps {
  /** Document ID - required for processing prompts */
  documentId?: string;
  /** Whether this is a new document (not yet saved) */
  isNewDocument: boolean;
  /** Optional tuned block id to scope the AI request. */
  focusBlockId?: string | null;
  /** Callback to set the editor value with the AI-edited document (user confirms and saves manually) */
  onValueUpdate: (value: EditorJsData | null) => void;
}

interface PromptCommandProps extends PromptCommandBaseProps {
  /** OpenAI model id for edit processor (must be in server allowlist). */
  chatModelId: string;
}

function useDocumentEditChatModelState() {
  const { aiAssistEditChatModels, defaultAiAssistEditChatModel } =
    useAppSettings();

  const modelOptions = React.useMemo(
    () =>
      aiAssistEditChatModels.length > 0
        ? aiAssistEditChatModels
        : [FALLBACK_DEFAULT_EDIT_MODEL],
    [aiAssistEditChatModels],
  );

  const [chatModelId, setChatModelId] = React.useState(
    FALLBACK_DEFAULT_EDIT_MODEL,
  );

  React.useEffect(() => {
    setChatModelId((prev) => {
      if (modelOptions.includes(prev)) return prev;
      if (modelOptions.includes(defaultAiAssistEditChatModel)) {
        return defaultAiAssistEditChatModel;
      }
      return modelOptions[0] ?? FALLBACK_DEFAULT_EDIT_MODEL;
    });
  }, [modelOptions, defaultAiAssistEditChatModel]);

  return { chatModelId, setChatModelId, modelOptions };
}

function EditChatModelField({
  chatModelId,
  setChatModelId,
  modelOptions,
  disabled,
}: {
  chatModelId: string;
  setChatModelId: (id: string) => void;
  modelOptions: string[];
  disabled?: boolean;
}) {
  return (
    <Box>
      <Text fontSize="xs" color="fg.muted" mb={1}>
        Model
      </Text>
      <select
        value={chatModelId}
        onChange={(e) => setChatModelId(e.target.value)}
        disabled={disabled}
        style={{ width: '100%' }}
      >
        {modelOptions.map((id) => (
          <option key={id} value={id}>
            {id}
          </option>
        ))}
      </select>
    </Box>
  );
}

function usePromptSubmit({
  documentId,
  isNewDocument,
  focusBlockId,
  onValueUpdate,
  chatModelId,
}: PromptCommandProps) {
  const [executeProcessor, { loading: processingPrompt }] =
    useMutation(EXECUTE_PROCESSOR);

  const disabled = processingPrompt || isNewDocument;

  const handleSubmit = React.useCallback(
    async (promptText: string) => {
      if (!promptText.trim()) return;
      if (!documentId) {
        toaster.create({
          title: 'Error',
          description: 'Save the document first to use AI modifications.',
          type: 'error',
        });
        return;
      }

      try {
        const { data } = await executeProcessor({
          variables: {
            input: {
              processorId: DOCUMENT_AI_EDIT_PROCESSOR_ID,
              variables: {
                input: promptText.trim(),
                documentId,
                model: chatModelId,
                ...(focusBlockId ? { focusBlockId } : {}),
              },
            },
          },
        });

        const result = data?.executeProcessor;
        if (result?.success && result?.result != null) {
          const value =
            typeof result.result === 'object' && 'value' in result.result
              ? result.result.value
              : undefined;
          if (typeof value === 'string') {
            const parsed = parseEditorJs(value);
            if (!parsed) {
              throw new Error('Processor did not return valid Editor.js JSON');
            }
            onValueUpdate(parsed);
            const summary =
              typeof result.result.message === 'string'
                ? result.result.message.trim()
                : '';
            toaster.create({
              title: 'Success',
              description: summary || FALLBACK_AI_EDIT_TOAST_DESCRIPTION,
            });
          } else {
            throw new Error('Processor did not return document value');
          }
        } else {
          throw new Error(result?.error ?? 'Processor failed');
        }
      } catch (error) {
        toaster.create({
          title: 'Error',
          description: `Error applying AI edit: ${
            error instanceof Error ? error.message : 'Unknown error'
          }`,
          type: 'error',
        });
      }
    },
    [chatModelId, documentId, focusBlockId, onValueUpdate, executeProcessor],
  );

  return { handleSubmit, disabled, processingPrompt };
}

interface UseAskSubmitParams {
  documentId?: string;
  isNewDocument: boolean;
}

function useAskSubmit({ documentId, isNewDocument }: UseAskSubmitParams) {
  const [executeProcessor, { loading: processingPrompt }] =
    useMutation(EXECUTE_PROCESSOR);
  const [askResult, setAskResult] = React.useState<string | null>(null);

  const disabled = processingPrompt || isNewDocument;

  const handleAskSubmit = React.useCallback(
    async (promptText: string) => {
      if (!promptText.trim()) return;
      if (!documentId) {
        toaster.create({
          title: 'Error',
          description: 'Save the document first to use AI.',
          type: 'error',
        });
        return;
      }

      try {
        const { data } = await executeProcessor({
          variables: {
            input: {
              processorId: ASK_DOCUMENT_PROCESSOR_ID,
              variables: {
                input: promptText.trim(),
                documentId,
              },
            },
          },
        });

        const result = data?.executeProcessor;
        if (result?.success && result?.result != null) {
          const message =
            typeof result.result === 'object' && 'message' in result.result
              ? result.result.message
              : undefined;
          if (typeof message === 'string') {
            setAskResult(message);
          } else {
            throw new Error('Processor did not return a message');
          }
        } else {
          throw new Error(result?.error ?? 'Processor failed');
        }
      } catch (error) {
        toaster.create({
          title: 'Error',
          description: `Error: ${
            error instanceof Error ? error.message : 'Unknown error'
          }`,
          type: 'error',
        });
      }
    },
    [documentId, executeProcessor],
  );

  const clearAskResult = React.useCallback(() => setAskResult(null), []);

  return {
    handleAskSubmit,
    askResult,
    clearAskResult,
    disabled,
    processingPrompt,
  };
}

/**
 * Inline AI prompt input for the document page footer (textarea + Apply).
 */
export function PromptFooterInput(props: PromptCommandBaseProps) {
  const { isNewDocument } = props;
  const [prompt, setPrompt] = React.useState('');
  const { chatModelId, setChatModelId, modelOptions } =
    useDocumentEditChatModelState();
  const { handleSubmit, disabled, processingPrompt } = usePromptSubmit({
    ...props,
    chatModelId,
  });

  const onSubmit = async () => {
    const text = prompt.trim();
    if (!text) return;
    setPrompt('');
    await handleSubmit(text);
  };

  return (
    <Box width="100%">
      <EditChatModelField
        chatModelId={chatModelId}
        setChatModelId={setChatModelId}
        modelOptions={modelOptions}
        disabled={disabled}
      />
      <HStack gap={2} align="flex-end" width="100%" mt={2}>
        <Textarea
          flex={1}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder={
            isNewDocument
              ? 'Save the document first to use AI modifications'
              : 'e.g., Summarize this content, Translate to Spanish, Fix grammar errors...'
          }
          rows={2}
          resize="none"
          minH="40px"
          maxH="120px"
          disabled={disabled}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              onSubmit();
            }
          }}
        />
        <Button
          size="sm"
          colorScheme="purple"
          onClick={onSubmit}
          disabled={!prompt.trim() || disabled}
          loading={processingPrompt}
          loadingText="Applying..."
          flexShrink={0}
        >
          Apply
        </Button>
      </HStack>
    </Box>
  );
}

/**
 * Prompt button (popover) for use where inline footer is not shown.
 */
export function PromptButton(props: PromptCommandBaseProps) {
  const { isNewDocument } = props;
  const [prompt, setPrompt] = React.useState('');
  const [isOpen, setIsOpen] = React.useState(false);
  const { chatModelId, setChatModelId, modelOptions } =
    useDocumentEditChatModelState();
  const { handleSubmit, disabled, processingPrompt } = usePromptSubmit({
    ...props,
    chatModelId,
  });

  const title = isNewDocument
    ? 'Save the document first to use AI modifications'
    : undefined;

  const onPopoverSubmit = async () => {
    if (!prompt.trim()) return;
    const promptText = prompt.trim();
    setPrompt('');
    setIsOpen(false);
    await handleSubmit(promptText);
  };

  const handleCancel = () => {
    setPrompt('');
    setIsOpen(false);
  };

  return (
    <Popover.Root
      open={isOpen}
      onOpenChange={(e) => setIsOpen(e.open)}
      positioning={{ placement: 'bottom-start' }}
      size="sm"
    >
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label="AI prompt to edit document"
          title={title ?? 'Use AI to edit this document'}
          disabled={disabled}
          onClick={() => setIsOpen(true)}
        >
          {processingPrompt ? (
            <Spinner size="sm" />
          ) : (
            <FiZap style={{ width: '24px', height: '24px' }} />
          )}
        </button>
      </Popover.Trigger>
      <Portal>
        <Popover.Positioner>
          <Popover.Content
            maxW="400px"
            p={4}
            bg="bg"
            border="1px solid"
            borderColor="border"
            shadow="lg"
            borderRadius="md"
          >
            <Popover.Arrow>
              <Popover.ArrowTip />
            </Popover.Arrow>
            <VStack gap={3} align="stretch">
              <Text fontSize="sm" fontWeight="semibold" color="fg">
                AI Prompt
              </Text>
              <Text fontSize="xs" color="fg.muted">
                Provide instructions for how AI should modify this document
              </Text>
              <EditChatModelField
                chatModelId={chatModelId}
                setChatModelId={setChatModelId}
                modelOptions={modelOptions}
                disabled={disabled}
              />
              <Textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="e.g., Summarize this content, Translate to Spanish, Fix grammar errors..."
                rows={4}
                resize="vertical"
                minH="100px"
              />
              <HStack gap={2} justify="flex-end">
                <Button size="sm" variant="ghost" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  colorScheme="purple"
                  onClick={onPopoverSubmit}
                  disabled={!prompt.trim()}
                >
                  Apply
                </Button>
              </HStack>
            </VStack>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
}

export interface AIAssistModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  documentId: string | undefined;
  isNewDocument: boolean;
}

type AssistMode = 'ask' | 'edit';

/**
 * AI Assist modal for the document header. Must be rendered inside EditDocumentProvider.
 * Toggle between Ask (returns AI message below) and Edit (applies edits to editor; save to persist).
 */
export function AIAssistModal({
  open,
  onOpenChange,
  documentId,
  isNewDocument,
}: AIAssistModalProps) {
  const { setValue } = useEditDocument();
  const [prompt, setPrompt] = React.useState('');
  const [mode, setMode] = React.useState<AssistMode>('ask');
  const { chatModelId, setChatModelId, modelOptions } =
    useDocumentEditChatModelState();

  const onValueUpdate = React.useCallback(
    (value: EditorJsData | null) => {
      setValue(value);
      onOpenChange(false);
    },
    [setValue, onOpenChange],
  );

  const {
    handleSubmit,
    disabled: editDisabled,
    processingPrompt: editLoading,
  } = usePromptSubmit({
    documentId,
    isNewDocument,
    onValueUpdate,
    chatModelId,
  });

  const {
    handleAskSubmit,
    askResult,
    clearAskResult,
    disabled: askDisabled,
    processingPrompt: askLoading,
  } = useAskSubmit({ documentId, isNewDocument });

  const isEditMode = mode === 'edit';
  const disabled = isEditMode ? editDisabled : askDisabled;
  const processingPrompt = isEditMode ? editLoading : askLoading;

  React.useEffect(() => {
    if (mode === 'edit') clearAskResult();
  }, [mode, clearAskResult]);

  const handleApply = React.useCallback(async () => {
    const text = prompt.trim();
    if (!text) return;
    setPrompt('');
    if (mode === 'edit') {
      await handleSubmit(text);
    } else {
      await handleAskSubmit(text);
    }
  }, [prompt, mode, handleSubmit, handleAskSubmit]);

  const handleClose = React.useCallback(() => {
    setPrompt('');
    clearAskResult();
    onOpenChange(false);
  }, [onOpenChange, clearAskResult]);

  return (
    <DialogRoot
      open={open}
      onOpenChange={(e) => {
        if (!e.open) handleClose();
      }}
      size="md"
    >
      <CenteredDialogContent>
        <DialogHeader>
          <DialogTitle>AI Assist</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <VStack gap={3} align="stretch">
            <HStack gap={2}>
              <Button
                size="sm"
                variant={mode === 'ask' ? 'solid' : 'outline'}
                colorScheme="purple"
                onClick={() => setMode('ask')}
              >
                Ask
              </Button>
              <Button
                size="sm"
                variant={mode === 'edit' ? 'solid' : 'outline'}
                colorScheme="purple"
                onClick={() => setMode('edit')}
              >
                Edit
              </Button>
            </HStack>
            <Text fontSize="sm" color="fg.muted">
              {mode === 'ask'
                ? 'Ask a question about this document. The answer will appear below.'
                : 'Describe how you want to modify this document. The edit will be applied in the editor; save to persist.'}
            </Text>
            {mode === 'edit' && (
              <EditChatModelField
                chatModelId={chatModelId}
                setChatModelId={setChatModelId}
                modelOptions={modelOptions}
                disabled={editDisabled}
              />
            )}
            <Textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={
                isNewDocument
                  ? 'Save the document first to use AI'
                  : mode === 'ask'
                    ? 'e.g., What are the main points? Summarize the budget section...'
                    : 'e.g., Summarize this content, Translate to Spanish, Fix grammar errors...'
              }
              rows={4}
              resize="vertical"
              minH="100px"
              disabled={disabled}
            />
            {mode === 'ask' && askResult != null && (
              <Box
                p={3}
                borderRadius="md"
                bg="bg.muted"
                borderWidth="1px"
                borderColor="border"
                maxH="200px"
                overflowY="auto"
              >
                <Text fontSize="sm" whiteSpace="pre-wrap">
                  {askResult}
                </Text>
              </Box>
            )}
          </VStack>
        </DialogBody>
        <DialogFooter>
          <DialogActionTrigger asChild>
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
          </DialogActionTrigger>
          <Button
            colorScheme="purple"
            onClick={handleApply}
            disabled={!prompt.trim() || disabled}
            loading={processingPrompt}
            loadingText={mode === 'ask' ? 'Asking...' : 'Applying...'}
          >
            {mode === 'ask' ? 'Ask' : 'Apply'}
          </Button>
        </DialogFooter>
      </CenteredDialogContent>
    </DialogRoot>
  );
}

export interface MakeChangeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  documentId: string | undefined;
  isNewDocument: boolean;
  focusBlockId: string | null;
}

/**
 * AI edit modal scoped to a single tuned Editor.js block.
 */
export function MakeChangeModal({
  open,
  onOpenChange,
  documentId,
  isNewDocument,
  focusBlockId,
}: MakeChangeModalProps) {
  const { setValue } = useEditDocument();
  const [prompt, setPrompt] = React.useState('');
  const { chatModelId, setChatModelId, modelOptions } =
    useDocumentEditChatModelState();

  const onValueUpdate = React.useCallback(
    (value: EditorJsData | null) => {
      setValue(value);
      onOpenChange(false);
    },
    [setValue, onOpenChange],
  );

  const { handleSubmit, disabled, processingPrompt } = usePromptSubmit({
    documentId,
    isNewDocument,
    focusBlockId,
    onValueUpdate,
    chatModelId,
  });

  const handleApply = React.useCallback(async () => {
    const text = prompt.trim();
    if (!text) return;
    setPrompt('');
    await handleSubmit(text);
  }, [prompt, handleSubmit]);

  const handleClose = React.useCallback(() => {
    setPrompt('');
    onOpenChange(false);
  }, [onOpenChange]);

  return (
    <DialogRoot
      open={open}
      onOpenChange={(e) => {
        if (!e.open) handleClose();
      }}
      size="md"
    >
      <CenteredDialogContent>
        <DialogHeader>
          <DialogTitle>Make Change</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <VStack gap={3} align="stretch">
            {focusBlockId ? (
              <Text fontSize="sm" color="fg.muted">
                The user has focused on ID{' '}
                <span style={{ fontFamily: 'monospace' }}>{focusBlockId}</span>{' '}
                in the document for making this request.
              </Text>
            ) : (
              <Text fontSize="sm" color="fg.muted">
                Describe the change you want to make.
              </Text>
            )}
            <EditChatModelField
              chatModelId={chatModelId}
              setChatModelId={setChatModelId}
              modelOptions={modelOptions}
              disabled={disabled}
            />
            <Textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={
                isNewDocument
                  ? 'Save the document first to use AI'
                  : 'e.g., Replace this section with a revised version; keep the tone consistent.'
              }
              rows={4}
              resize="vertical"
              minH="100px"
              disabled={disabled}
            />
          </VStack>
        </DialogBody>
        <DialogFooter>
          <DialogActionTrigger asChild>
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
          </DialogActionTrigger>
          <Button
            colorScheme="purple"
            onClick={handleApply}
            disabled={!prompt.trim() || disabled}
            loading={processingPrompt}
            loadingText="Applying..."
          >
            Apply
          </Button>
        </DialogFooter>
      </CenteredDialogContent>
    </DialogRoot>
  );
}
